import React, { ReactElement, useEffect, useState } from 'react';
import { MenuProps } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { queryKeysClient } from 'common/clients/query-key.client';
import { queryClient } from 'common/clients/query.client';
import { ExtendedTable } from 'common/components/ExtendedTable/ExtendedTable';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from 'common/constants/table.constants';
import { EQueryKeys } from 'common/enums/query-kyes.enum';
import { removeEmpty } from 'common/utils/object.utils';
import { ECounterpartyAction, ECounterpartyColumnKeys } from 'domains/counterparties/constants';
import { useGetCounterpartiesQuery } from 'domains/counterparties/queries/counterparty.query';
import {
  TCounterpartiesListResData,
  TCounterpartyEntity,
  TCounterpartyReqParams
} from 'domains/counterparties/types/counterparty.types';
import { ROUTES_MAPPING } from 'navigation/constants/route.constants';
import { FiltersFormComponent } from './components/filters-form/filters-form.component';
import {
  SDangerActionItem,
} from './counterparties.page.styles';
import {
  ECounterpartiesFilterFormKeys,
  EModalType,
  EUserManagementCounterpartiesPageGetParams,
  EUserManagementCounterpartiesPageGetParamsActionTypes,
} from './enums/conterparties.enums';
import CreateEditCounterpartyModal from './modals/create-edit-counterparty.modal';
import { getColumns } from './tables/counterparties-table.columns';
import {
  TCounterpartyModalData,
  TCounterpartyTableRecord,
  TTablePaginationConfig,
} from './types/counterparties.types';

const UserRoleManagementCounterpartiesPage = (): ReactElement => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [form] = useForm<ECounterpartiesFilterFormKeys>();
  const [modalData, setModalData] = useState<TCounterpartyModalData | {}>({});
  const [submitFilter, setSubmitFilter] = useState<boolean>(false);
  const [_filterValues, setFilterValues] = useState<TCounterpartyReqParams>({});
  const filterValues = removeEmpty(_filterValues);
  const [paginationData, setPaginationData] = useState<TTablePaginationConfig>({
    page: DEFAULT_PAGE,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const {
    data: counterpartiesData,
    isLoading: isCounterpartiesDataLoading,
    isError: isCounterpartiesDataError,
    error: counterpartiesDataError,
  } = useGetCounterpartiesQuery(
    {
      limit: paginationData.pageSize,
      offset: (paginationData.page - 1) * paginationData.pageSize,
      ...filterValues,
    },
    {
      refetchOnMount: false,
    },
  );

  useEffect(() => {
    if (isCounterpartiesDataError) {
      console.error(counterpartiesDataError);
    }
  }, [isCounterpartiesDataError, counterpartiesDataError]);

  useEffect(() => {
    const searchParams = new URLSearchParams(document.location.search);
    const pageAction = searchParams.get(EUserManagementCounterpartiesPageGetParams.Action);

    if (pageAction === EUserManagementCounterpartiesPageGetParamsActionTypes.Create) {
      setModalData({
        type: EModalType.Create,
        rowData: {},
        counterpartiesData,
      });
    }
  }, [document.location.search, counterpartiesData]);

  const actions: MenuProps['items'] = [
    {
      key: ECounterpartyAction.Edit,
      label: t('usrRoleMng.counterparties.table.menu.action.edit'),
    },
    {
      key: ECounterpartyAction.Delete,
      label: <SDangerActionItem>{t('usrRoleMng.counterparties.table.menu.action.delete')}</SDangerActionItem>,
    },
  ];

  const onItemActionClick = ({ record, actionKey }: { record: TCounterpartyTableRecord, actionKey: string }): void => {
    switch (actionKey) {
      case ECounterpartyAction.Edit: {
        setModalData({
          type: EModalType.Edit,
          rowData: record,
          counterpartiesData,
        });
        break;
      }
      case ECounterpartyAction.Delete: {
        throw new Error('Deletion is not implemented');
        break;
      }
      default: {
        break;
      }
    }
  };

  const columns = getColumns({
    menuItems: actions,
    onItemActionClick,
  });

  const onPaginationChangeHandler = (pageParam: number, pageSizeParam: number): void => {
    setPaginationData({
      page: pageParam,
      pageSize: pageSizeParam,
    });
  };

  const paginationConfig = {
    style: {
      marginRight: 30,
    },
    showQuickJumper: true,
    showSizeChanger: true,
    total: counterpartiesData?.total || 0,
    pageSize: paginationData.pageSize,
    current: paginationData.page,
    onChange: onPaginationChangeHandler,
  };

  const onCancelModalHandler = (): void => {
    setModalData({});
    navigate(ROUTES_MAPPING.PROTECTED.USER_ROLE_MANAGEMENT_COUNTERPARTIES);
  };

  const counterpartyTableData: TCounterpartyTableRecord[] | undefined = counterpartiesData?.counterparties?.map(
    (item: TCounterpartyEntity): TCounterpartyTableRecord => ({
      ...item,
      code: item?.code ?? `12345${item.name}`, // mock
      logo_url: 'https://public-files-dcm-pre-prod.s3.eu-central-1.amazonaws.com/preprod-admin.png', // mock
    }),
  );

  const onSubmit = (values: Partial<Record<ECounterpartiesFilterFormKeys, string>>): void => {
    const valuesFromForm = {
      ...values,
    } as TCounterpartyReqParams;

    const valuesToSend = removeEmpty(valuesFromForm);

    setPaginationData((currentPaginationData) => ({
      page: DEFAULT_PAGE,
      pageSize: currentPaginationData.pageSize,
    }));

    setFilterValues(valuesToSend);
    setSubmitFilter(true);
  };

  useEffect(() => {
    setSubmitFilter(false);
  }, [filterValues]);

  return (
    <>
      <ExtendedTable
        pagination={paginationConfig}
        loading={isCounterpartiesDataLoading}
        rowKey={ECounterpartyColumnKeys.Guid}
        dataSource={(counterpartyTableData || []) as TCounterpartyTableRecord[]}
        columns={columns}
        hideColumnsBtn
        hideFiltersBtn={false}
        submitFilter={submitFilter}
        filtersElem={
          <FiltersFormComponent
            onSubmit={onSubmit}
            form={form}
            isLoading={isCounterpartiesDataLoading}
          />
        }
      />
      <CreateEditCounterpartyModal
        isOpen={!!Object.keys(modalData).length}
        onCancelModalHandler={onCancelModalHandler}
        data={modalData as TCounterpartyModalData}
      />
    </>
  );
};

export const loader = async (): Promise<{ counterpartiesData: TCounterpartiesListResData | {} }> => {
  try {
    const counterpartiesListQuery =
      queryKeysClient[EQueryKeys.CounterpartyQueryKeys].getCounterpartiesList({
        offset: (DEFAULT_PAGE - 1) * DEFAULT_PAGE_SIZE,
        limit: DEFAULT_PAGE_SIZE,
      });
    const counterpartiesData: TCounterpartiesListResData =
      queryClient.getQueryData(counterpartiesListQuery.queryKey) ??
      (await queryClient.fetchQuery(counterpartiesListQuery));

    return {
      counterpartiesData,
    };
  } catch (error) {
    console.error(error);

    return {
      counterpartiesData: {},
    }
  }
};

export default UserRoleManagementCounterpartiesPage;
